import BodyText from '@lyra/core/components/BodyText'
import Countdown from '@lyra/core/components/Countdown'
import LabelText from '@lyra/core/components/LabelText'
import Link from '@lyra/core/components/Link'
import Section from '@lyra/core/components/Section'
import TooltipText from '@lyra/core/components/TooltipText'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatUSD from '@lyra/core/utils/formatUSD'
import CopyButton from '@lyra/web/components/common/CopyButton'
import PayoffChart, { PayoffLegData } from '@lyra/web/components/common/PayoffChart'
import { isMainnet } from '@lyra/web/constants/env'
import { Ticker } from '@lyra/web/constants/instruments'
import {
  DEFAULT_RFQ_SIZE,
  RfqBestQuote,
  SendRfqParams,
  SendRfqResponse,
} from '@lyra/web/constants/rfqs'
import { HELP_FEES_URL } from '@lyra/web/constants/urls'
import TradeFormRewardsRow from '@lyra/web/containers/trade/TradeFormRewardsRow'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { getBestOfferPrice } from '@lyra/web/utils/instruments'
import { getOrderQuotePremiums } from '@lyra/web/utils/order'
import { getRfqBestBidAskOffer, getRfqGcdAmount, getRfqMarkValue } from '@lyra/web/utils/rfqs'
import { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

type Props = {
  rfq: SendRfqParams | SendRfqResponse
  tickers?: Record<string, Ticker>
  quote?: RfqBestQuote | null
  isLoading?: boolean
}

const RfqTradeFormReceipt = ({ rfq, tickers, quote, isLoading }: Props) => {
  const markValue = tickers ? getRfqMarkValue(tickers, rfq.legs) : 0

  const amount = useMemo(() => {
    const amount = getRfqGcdAmount(rfq.legs)
    return amount ? amount : DEFAULT_RFQ_SIZE
  }, [rfq.legs])

  const fees = quote ? +quote.estimated_fee : 0
  const baseFee = tickers ? +Object.values(tickers)[0].base_fee : 0
  const totalCost = quote ? +quote.estimated_total_cost : 0

  const spotPrice = tickers ? +tickers[Object.keys(tickers)[0]].index_price : 0

  const totalCostPerSpread = amount !== 0 && totalCost ? totalCost / amount : 0

  const pnl = quote ? +quote.estimated_realized_pnl : 0

  const bestOffer = tickers ? getRfqBestBidAskOffer(tickers, rfq.legs, true) : 0
  const bestOfferPerSpread = amount !== 0 && bestOffer ? bestOffer / amount : 0

  const markValuePerSpread = amount !== 0 ? markValue / amount : 0

  const liquidationPrice = quote?.post_liquidation_price ? +quote.post_liquidation_price : 0

  const upLiquidationPrice = quote && quote?.up_liquidation_price ? +quote.up_liquidation_price : 0
  const downLiquidationPrice =
    quote && quote?.down_liquidation_price ? +quote.down_liquidation_price : 0

  // set mark value to $0 if sign is not equal to total cost
  const markValuePerSpreadAbs =
    !totalCostPerSpread || Math.sign(totalCostPerSpread) === Math.sign(markValuePerSpread)
      ? Math.abs(markValuePerSpread)
      : 0

  // set best offer to $0 if sign is not equal to total cost
  const bestOfferPerSpreadAbs =
    !totalCostPerSpread || Math.sign(totalCostPerSpread) === Math.sign(bestOfferPerSpread)
      ? Math.abs(bestOfferPerSpread)
      : 0

  const bestQuote = quote?.best_quote

  const payoffLegs = useMemo(() => {
    if (!rfq.legs.length) {
      return null
    }
    if (!tickers) {
      return null
    }
    const payoffLegs: (PayoffLegData | null)[] = filterNulls(
      rfq.legs.map((leg) => {
        const bestQuoteLeg = bestQuote?.legs.find(
          (thisLeg) =>
            thisLeg.instrument_name === leg.instrument_name && thisLeg.direction === leg.direction
        )

        const ticker = tickers[leg.instrument_name]
        if (!ticker) {
          return null
        }

        const bbo = getBestOfferPrice(ticker, leg.direction === 'buy')
        const estFillPrice = bestQuoteLeg ? +bestQuoteLeg.price : bbo ? bbo : +ticker.mark_price

        const premiums = getOrderQuotePremiums(
          estFillPrice,
          +leg.amount,
          leg.direction === 'buy',
          0
        )

        return {
          instrument_name: leg.instrument_name,
          direction: leg.direction,
          amount: +leg.amount,
          premiums,
        }
      })
    )

    if (payoffLegs.length !== rfq.legs.length || !payoffLegs.length) {
      // something got filtered
      return null
    }

    return payoffLegs as [PayoffLegData, ...PayoffLegData[]]
  }, [rfq.legs, bestQuote, tickers])

  const { subaccount } = useSubaccount()
  const hasOpenPositions = useMemo(() => {
    return rfq.legs.some((leg) => {
      return !!subaccount?.positions
        .filter((p) => !!+p.amount)
        .find((pos) => pos.instrument_name === leg.instrument_name)
    })
  }, [subaccount, rfq.legs])

  return (
    <>
      <Section.YStack>
        {payoffLegs && !hasOpenPositions ? (
          <>
            <LabelText>Expected Profit / Loss</LabelText>
            <PayoffChart height={110} legs={payoffLegs} spotPrice={spotPrice} />
          </>
        ) : null}
        {pnl ? (
          <XStack justifyContent="space-between">
            <LabelText>Profit / Loss</LabelText>
            <BodyText color={pnl > 0 ? 'green' : 'red'}>
              {formatUSD(pnl, { showSign: true })}
            </BodyText>
          </XStack>
        ) : null}
        {isMainnet ? <TradeFormRewardsRow fee={fees} baseFee={baseFee} /> : null}
        {bestQuote ? (
          <XStack justifyContent="space-between">
            <TooltipText
              label="Fees"
              tooltipContent={
                <BodyText color="secondary">
                  <Link href={HELP_FEES_URL} label="View maker / taker fees" />
                </BodyText>
              }
              stayOpenOnHover
              placement="top-end"
            />
            <BodyText>{isLoading ? '...' : formatUSD(fees)}</BodyText>
          </XStack>
        ) : null}
        {bestQuote ? (
          upLiquidationPrice && downLiquidationPrice ? (
            <>
              <XStack justifyContent="space-between">
                <LabelText>Up Liquidation Price</LabelText>
                <BodyText>
                  {isLoading ? '...' : upLiquidationPrice ? formatUSD(upLiquidationPrice) : 'None'}
                </BodyText>
              </XStack>
              <XStack justifyContent="space-between">
                <LabelText>Down Liquidation Price</LabelText>
                <BodyText>
                  {isLoading
                    ? '...'
                    : downLiquidationPrice
                    ? formatUSD(downLiquidationPrice)
                    : 'None'}
                </BodyText>
              </XStack>
            </>
          ) : (
            <XStack justifyContent="space-between">
              <LabelText>Liquidation Price</LabelText>
              <BodyText>
                {isLoading ? '...' : liquidationPrice ? formatUSD(liquidationPrice) : 'None'}
              </BodyText>
            </XStack>
          )
        ) : null}
        {bestQuote ? (
          <XStack justifyContent="space-between">
            <TooltipText
              label="Price Per Option"
              tooltipContent="The quoted price per spread."
              placement="top-end"
            />
            <BodyText>{isLoading ? '...' : formatUSD(Math.abs(totalCostPerSpread))}</BodyText>
          </XStack>
        ) : null}
        <XStack justifyContent="space-between">
          <TooltipText
            label="Mark Price"
            tooltipContent="The fair price per spread based on oracle prices."
            placement="top-end"
          />
          <BodyText>{isLoading ? '...' : formatUSD(Math.abs(markValuePerSpreadAbs))}</BodyText>
        </XStack>
        <XStack justifyContent="space-between">
          <TooltipText
            label="BBO"
            tooltipContent="The price per spread based on the sum of the best bid / ask offers on the orderbook. If an option is missing a best bid / ask offer, this value is ignored."
            placement="top-end"
          />
          <BodyText>{isLoading ? '...' : formatUSD(Math.abs(bestOfferPerSpreadAbs))}</BodyText>
        </XStack>
        <XStack justifyContent="space-between">
          <TooltipText
            label="Developers"
            stayOpenOnHover
            tooltipContent={
              <YStack gap="$1">
                <XStack gap="$2" alignItems="center">
                  <BodyText color="secondary">Copy Parameters</BodyText>
                  <CopyButton marginLeft="auto" value={JSON.stringify(rfq)} size="sm" />
                </XStack>
                {'rfq_id' in rfq ? (
                  <XStack gap="$2" alignItems="center">
                    <BodyText color="secondary">RFQ ID</BodyText>
                    <BodyText marginLeft="auto">{rfq.rfq_id.substring(0, 10) + '...'}</BodyText>
                    <CopyButton value={rfq.rfq_id} size="sm" />
                  </XStack>
                ) : null}
                {'rfq_id' in rfq ? (
                  <XStack gap="$2" alignItems="center">
                    <BodyText color="secondary">Subaccount ID</BodyText>
                    <BodyText marginLeft="auto">{rfq.subaccount_id}</BodyText>
                    <CopyButton value={rfq.rfq_id} size="sm" />
                  </XStack>
                ) : null}
                <XStack gap="$2" justifyContent="space-between" alignItems="center">
                  <BodyText color="secondary">Max Total Cost</BodyText>
                  <BodyText>
                    {rfq.max_total_cost ? formatUSD(+rfq.max_total_cost) : 'Not Set'}
                  </BodyText>
                </XStack>
                <XStack gap="$2" justifyContent="space-between" alignItems="center">
                  <BodyText color="secondary">Counterparties</BodyText>
                  <BodyText>
                    {rfq.counterparties ? JSON.stringify(rfq.counterparties) : 'Any'}
                  </BodyText>
                </XStack>
                {'rfq_id' in rfq ? (
                  <XStack gap="$2" justifyContent="space-between" alignItems="center">
                    <BodyText color="secondary">Valid Until</BodyText>
                    <BodyText>
                      <Countdown endTimestamp={rfq.valid_until} />
                    </BodyText>
                  </XStack>
                ) : null}
              </YStack>
            }
            placement="top-end"
          />
        </XStack>
      </Section.YStack>
    </>
  )
}

export default RfqTradeFormReceipt
